import React, { useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { AdminDashboardHeader } from '../AdminDashboard/AdminDashboardHeader';

// Import all components for preview
import { NavbarComponent } from '../../components/NavbarComponent';
import { HeroComponent } from '../../components/HeroComponent';
import { FeaturesComponent } from '../../components/FeaturesComponent';
import { FooterComponent } from '../../components/FooterComponent';
import { CtaComponent } from '../../components/CtaComponent';
import { FormComponent } from '../../components/FormComponent';
import { PricingComponent } from '../../components/PricingComponent';
import { TeamComponentRenderer as TeamComponent } from '../../components/TeamComponent';
import { MediaTextComponent } from '../../components/MediaTextComponent';
import { TextBlocksComponent } from '../../components/TextBlocksComponent';
import { StatsComponent } from '../../components/StatsComponent';

import { useThemeManagerState } from './useThemeManagerState';
import { createThemeManagerHandlers } from './useThemeManagerHandlers';
import { ThemeManagerHeader } from './ThemeManagerHeader';
import { ThemeManagerToasts } from './ThemeManagerToasts';
import { ThemeManagerControls } from './ThemeManagerControls';
import { ThemeManagerPreview } from './ThemeManagerPreview';

const components = [
  { id: 'navbar', name: 'Navigation Bar', component: NavbarComponent },
  { id: 'hero', name: 'Hero Section', component: HeroComponent },
  { id: 'features', name: 'Features', component: FeaturesComponent },
  { id: 'cta', name: 'Call to Action', component: CtaComponent },
  { id: 'pricing', name: 'Pricing', component: PricingComponent },
  { id: 'stats', name: 'Stats', component: StatsComponent },
  { id: 'footer', name: 'Footer', component: FooterComponent },
  { id: 'form', name: 'Form', component: FormComponent },
  { id: 'team', name: 'Team', component: TeamComponent },
  { id: 'mediaText', name: 'Media & Text', component: MediaTextComponent },
  { id: 'textBlocks', name: 'Text Blocks', component: TextBlocksComponent },
];

export const ThemeManager: React.FC = () => {
  const { logout } = useAuth();
  const state = useThemeManagerState();
  const handlers = createThemeManagerHandlers({ state });

  // Load themes from API
  useEffect(() => {
    handlers.loadThemes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load theme details when selection changes
  useEffect(() => {
    if (state.selectedTheme) {
      handlers.loadThemeDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedTheme]);

  const componentStyles = state.customColors[state.selectedComponent] || {};

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminDashboardHeader onLogout={logout} />
      <ThemeManagerHeader
        onImport={handlers.handleImport}
        onExport={handlers.handleExport}
        onCopy={handlers.copyThemeConfig}
        onReset={handlers.handleReset}
        onSave={handlers.handleSave}
        copied={state.copied}
        isResetting={state.isResetting}
        isSaving={state.isSaving}
      />

      <ThemeManagerToasts
        showSuccess={state.showSuccess}
        showResetSuccess={state.showResetSuccess}
        error={state.error}
        onDismissError={() => state.setError(null)}
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Loading State */}
        {state.isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary-600" />
              <p className="text-gray-600">Loading themes...</p>
            </div>
          </div>
        )}

        {!state.isLoading && (
          <>
            <ThemeManagerControls
              selectedTheme={state.selectedTheme}
              onThemeChange={state.setSelectedTheme}
              availableThemes={state.availableThemes}
              selectedComponent={state.selectedComponent}
              onComponentChange={state.setSelectedComponent}
              components={components}
              componentStyles={componentStyles}
              onColorChange={handlers.handleColorChange}
            />

            <ThemeManagerPreview
              selectedTheme={state.selectedTheme}
              selectedComponent={state.selectedComponent}
              customColors={state.customColors}
              components={components}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ThemeManager;
